import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const ROUTE_COLS = [
  { dot:'#378ADD', light:'#E6F1FB', text:'#0C447C' },
  { dot:'#1D9E75', light:'#E1F5EE', text:'#085041' },
  { dot:'#7F77DD', light:'#EEEDFE', text:'#3C3489' },
  { dot:'#D85A30', light:'#FAECE7', text:'#4A1B0C' },
]
const DAY_NAMES = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb']

function todayStr() { return new Date().toISOString().slice(0,10) }
function shiftDate(base, dir) {
  const d = new Date(base+'T12:00:00'); d.setDate(d.getDate()+dir); return d.toISOString().slice(0,10)
}
function fmtDate(str) {
  const d = new Date(str+'T12:00:00')
  return `${DAY_NAMES[d.getDay()]}, ${d.toLocaleDateString('es-MX',{month:'short',day:'numeric'})}`
}
function timeToMin(t) {
  if (!t) return 0
  const [hm,ap] = t.split(' '); const [h,m] = hm.split(':').map(Number)
  return ((h%12)+(ap==='PM'?12:0))*60+m
}

// Build a Google Maps directions URL with all stops in order
function buildDirectionsUrl(addresses) {
  if (!addresses.length) return null
  if (addresses.length === 1) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addresses[0])}`
  }
  const origin = encodeURIComponent(addresses[0])
  const dest = encodeURIComponent(addresses[addresses.length-1])
  const wps = addresses.slice(1,-1).map(encodeURIComponent).join('|')
  return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${dest}${wps?`&waypoints=${wps}`:''}&travelmode=driving`
}

// Build Google Maps embed URL showing all stops
// Uses the search embed to show a map centered on the area with the first address
function buildEmbedUrl(addresses, routeLabel) {
  if (!addresses.length) return null
  // Use directions embed — shows the full route on the map
  const base = 'https://www.google.com/maps/embed/v1/directions'
  const key = process.env.REACT_APP_GOOGLE_MAPS_KEY || ''
  if (addresses.length === 1) {
    return `https://www.google.com/maps/embed/v1/place?key=${key}&q=${encodeURIComponent(addresses[0])}&zoom=13`
  }
  const origin = encodeURIComponent(addresses[0])
  const dest = encodeURIComponent(addresses[addresses.length-1])
  const wps = addresses.slice(1,-1).map(encodeURIComponent).join('|')
  return `${base}?key=${key}&origin=${origin}&destination=${dest}${wps?`&waypoints=${wps}`:''}&mode=driving&zoom=11`
}

// Fallback: OpenStreetMap static map with markers
function buildOSMUrl(stops, W, H) {
  // Use a mapbox static image or just link to OSM
  const addrs = stops.map(s=>s.fullAddr).filter(Boolean)
  if (!addrs.length) return null
  // Build an OSM embed URL centered on Gardena/LA area
  return `https://www.openstreetmap.org/export/embed.html?bbox=-118.65%2C33.65%2C-117.85%2C34.15&layer=mapnik`
}

export default function MapView() {
  const [dateStr, setDateStr] = useState(todayStr())
  const [jobs, setJobs] = useState([])
  const [clients, setClients] = useState([])
  const [selectedRoute, setSelectedRoute] = useState('all')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetch() {
      setLoading(true)
      const [{ data: jb }, { data: cl }] = await Promise.all([
        supabase.from('jobs').select('*').eq('scheduled_date', dateStr),
        supabase.from('clients').select('*'),
      ])
      setJobs(jb||[])
      setClients(cl||[])
      setLoading(false)
    }
    fetch()
  }, [dateStr])

  const routeLabels = [...new Set(jobs.map(j=>j.route).filter(Boolean))].sort()

  const routeData = routeLabels.map((label, ri) => {
    const rc = ROUTE_COLS[ri % ROUTE_COLS.length]
    const routeJobs = jobs
      .filter(j=>j.route===label)
      .sort((a,b)=>timeToMin(a.start_time)-timeToMin(b.start_time))
    const stops = routeJobs.map(job => {
      const client = clients.find(c=>c.id===job.client_id)
      const fullAddr = client?.address
        ? `${client.address}${client.city?', '+client.city:''}`
        : null
      return { job, client, fullAddr }
    })
    const addresses = stops.map(s=>s.fullAddr).filter(Boolean)
    return { label, rc, stops, addresses }
  })

  const displayRoutes = selectedRoute==='all'
    ? routeData
    : routeData.filter(r=>r.label===selectedRoute)

  // For the embed: show all routes combined or just selected
  const allAddresses = displayRoutes.flatMap(r=>r.addresses)
  const embedUrl = allAddresses.length > 0 ? buildEmbedUrl(
    displayRoutes.length===1 ? displayRoutes[0].addresses : allAddresses,
    selectedRoute
  ) : null

  // Fallback OSM embed when no addresses
  const osmUrl = `https://www.openstreetmap.org/export/embed.html?bbox=-118.65%2C33.65%2C-117.85%2C34.15&layer=mapnik`

  return (
    <div style={s.wrap}>
      <div style={s.topbar}>
        <button style={s.nbtn} onClick={()=>setDateStr(d=>shiftDate(d,-1))}>←</button>
        <span style={s.dayLabel}>{fmtDate(dateStr)}</span>
        <button style={s.nbtn} onClick={()=>setDateStr(d=>shiftDate(d,1))}>→</button>
        <div style={{display:'flex',gap:4,marginLeft:12}}>
          <button style={{...s.rtab,...(selectedRoute==='all'?s.rtabOn:{})}} onClick={()=>setSelectedRoute('all')}>Todas</button>
          {routeLabels.map(l=>(
            <button key={l} style={{...s.rtab,...(selectedRoute===l?s.rtabOn:{})}} onClick={()=>setSelectedRoute(l)}>Ruta {l}</button>
          ))}
        </div>
        {/* Open in Google Maps button */}
        {displayRoutes.map(r=>(
          r.addresses.length>0&&(
            <a key={r.label} href={buildDirectionsUrl(r.addresses)} target="_blank" rel="noreferrer"
              style={{marginLeft:6,fontSize:11,padding:'5px 11px',background:r.rc.dot,color:'#fff',borderRadius:8,textDecoration:'none',fontWeight:500}}>
              Ruta {r.label} en Maps →
            </a>
          )
        ))}
      </div>

      <div style={s.body}>
        {/* MAP */}
        <div style={s.mapArea}>
          {loading ? (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',color:'#9aa0a6',gap:8}}>
              <span style={{fontSize:24}}>🌸</span> Cargando...
            </div>
          ) : displayRoutes.length===0 ? (
            <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',flexDirection:'column',gap:10,color:'#9aa0a6'}}>
              <div style={{fontSize:36}}>🗺️</div>
              <div style={{fontSize:14}}>Sin rutas para este día</div>
              <div style={{fontSize:12}}>Asigna trabajos a rutas y regresa aquí</div>
              {/* Show OSM base map anyway */}
              <iframe src={osmUrl} style={{position:'absolute',inset:0,width:'100%',height:'100%',border:'none',opacity:0.4}} title="Mapa base"/>
            </div>
          ) : allAddresses.length===0 ? (
            <div style={{position:'relative',width:'100%',height:'100%'}}>
              <iframe src={osmUrl} style={{width:'100%',height:'100%',border:'none'}} title="Mapa base"/>
              <div style={{position:'absolute',top:'50%',left:'50%',transform:'translate(-50%,-50%)',background:'rgba(255,255,255,0.92)',borderRadius:12,padding:'16px 20px',textAlign:'center',fontSize:13,color:'#5f6368',maxWidth:280}}>
                ⚠️ Los clientes no tienen direcciones guardadas.<br/>
                <span style={{fontSize:12,color:'#9aa0a6',marginTop:4,display:'block'}}>Agrega direcciones en Clientes para ver los pines en el mapa.</span>
              </div>
            </div>
          ) : embedUrl ? (
            <iframe
              src={embedUrl}
              style={{width:'100%',height:'100%',border:'none'}}
              title="Mapa de rutas"
              loading="lazy"
              allowFullScreen
            />
          ) : (
            <iframe src={osmUrl} style={{width:'100%',height:'100%',border:'none'}} title="Mapa"/>
          )}
        </div>

        {/* SIDEBAR */}
        <div style={s.sidebar}>
          {displayRoutes.length===0
            ? <div style={s.empty}>Sin rutas hoy</div>
            : displayRoutes.map(r=>(
              <div key={r.label} style={s.rSection}>
                <div style={{...s.rHead,borderLeft:`4px solid ${r.rc.dot}`,background:r.rc.light}}>
                  <span style={{fontSize:12,fontWeight:600,color:r.rc.dot}}>Ruta {r.label}</span>
                  <div style={{display:'flex',gap:6,alignItems:'center'}}>
                    <span style={{fontSize:10,color:r.rc.text}}>{r.stops.length} paradas</span>
                    {r.addresses.length>0&&(
                      <a href={buildDirectionsUrl(r.addresses)} target="_blank" rel="noreferrer"
                        style={{fontSize:10,color:r.rc.dot,textDecoration:'none',fontWeight:500}}>Maps →</a>
                    )}
                  </div>
                </div>
                {r.stops.map((stop,si)=>(
                  <div key={stop.job.id} style={s.stopRow}>
                    <div style={{...s.stopNum,background:r.rc.dot}}>{si+1}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={s.stopName}>{stop.client?.nickname||stop.client?.name||'Cliente'}</div>
                      <div style={s.stopMeta}>{stop.job.start_time} · {stop.job.hours}h</div>
                      {stop.fullAddr?(
                        <a href={`https://maps.google.com?q=${encodeURIComponent(stop.fullAddr)}`}
                          target="_blank" rel="noreferrer"
                          style={{fontSize:10,color:r.rc.dot,textDecoration:'none',display:'block'}}>
                          📍 {stop.client?.address?.split(',')[0]}
                        </a>
                      ):(
                        <span style={{fontSize:10,color:'#ddd'}}>Sin dirección</span>
                      )}
                      {stop.client?.entry_code&&(
                        <div style={{fontSize:10,color:'#9aa0a6'}}>🔑 {stop.client.entry_code}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
          ))}
          <div style={{padding:'10px',fontSize:10,color:'#9aa0a6',borderTop:'1px solid #e3e5e8',marginTop:4,lineHeight:1.6}}>
            🚗 Presiona "Maps →" para ver la ruta completa con navegación en Google Maps
          </div>
        </div>
      </div>
    </div>
  )
}

const s = {
  wrap:{display:'flex',flexDirection:'column',height:'100%',overflow:'hidden'},
  topbar:{display:'flex',alignItems:'center',gap:6,padding:'9px 14px',borderBottom:'1px solid #e3e5e8',background:'#fff',flexShrink:0,flexWrap:'wrap'},
  nbtn:{padding:'4px 10px',border:'1px solid #e3e5e8',borderRadius:8,background:'#f8f9fa',cursor:'pointer',fontSize:14,color:'#5f6368'},
  dayLabel:{fontSize:13,fontWeight:500,color:'#202124'},
  rtab:{fontSize:11,padding:'4px 10px',border:'1px solid #e3e5e8',borderRadius:8,background:'#fff',color:'#80868b',cursor:'pointer'},
  rtabOn:{background:'#fdeef3',color:'#c03060',borderColor:'#f4b8cc',fontWeight:500},
  body:{display:'flex',flex:1,overflow:'hidden'},
  mapArea:{flex:1,overflow:'hidden',position:'relative'},
  sidebar:{width:200,borderLeft:'1px solid #e3e5e8',overflowY:'auto',background:'#fff',flexShrink:0},
  empty:{padding:16,fontSize:12,color:'#9aa0a6',textAlign:'center'},
  rSection:{marginBottom:2},
  rHead:{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'7px 10px'},
  stopRow:{display:'flex',alignItems:'flex-start',gap:7,padding:'7px 10px',borderBottom:'1px solid #f8f9fa'},
  stopNum:{width:18,height:18,borderRadius:'50%',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:600,color:'#fff',flexShrink:0,marginTop:1},
  stopName:{fontSize:12,fontWeight:500,color:'#202124'},
  stopMeta:{fontSize:10,color:'#80868b',marginTop:1},
}
